import { useMemo } from "react";
import { Bar, BarChart, XAxis, YAxis } from "recharts";

import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import type { Metric } from "./metric-cell";

/** "2026-09-12" → "Sep 12"; other keys are shown as they are. */
function defaultLabel(key: string) {
  const date = new Date(key);
  if (Number.isNaN(date.getTime())) return key;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/** Short axis figures: 1.2k, 3.4M; money keeps its sign. */
function axisLabel(value: number, format?: (v: number) => string) {
  const abs = Math.abs(value);
  const short =
    abs >= 1_000_000
      ? `${(value / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`
      : abs >= 1_000
        ? `${(value / 1_000).toFixed(1).replace(/\.0$/, "")}k`
        : null;
  if (short) {
    const isMoney = format ? format(1).trim().startsWith("$") : false;
    return isMoney ? `$${short}` : short;
  }
  return format ? format(value) : String(value);
}

/**
 * The full-width chart a strip opens under itself when a figure is picked.
 * One series, brand bars on a hairline baseline, no grid.
 */
export function MetricChart({
  metric,
  color = "rgb(var(--brand-600))",
}: {
  metric: Metric;
  /** Series color; TikTok charts pass var(--chart-tiktok), the channel teal. */
  color?: string;
}) {
  const data = useMemo(
    () =>
      Object.entries(metric.series ?? {})
        .map(([key, value]) => ({ key, value: Number(value) || 0 }))
        .sort((a, b) => a.key.localeCompare(b.key))
        .map((p) => ({ ...p, label: (metric.labelFor ?? defaultLabel)(p.key) })),
    [metric.series, metric.labelFor]
  );
  const format = metric.format ?? ((v: number) => String(v));
  const unit = metric.unit ?? "";

  return (
    <div className="border-t border-border px-5 pb-4 pt-4">
      <ChartContainer
        config={{ value: { label: metric.label, color } }}
        className="h-44 w-full"
      >
        <BarChart data={data} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
          <XAxis
            dataKey="label"
            tickLine={false}
            axisLine={{ stroke: "hsl(var(--border))" }}
            tick={{ fontSize: 11 }}
            interval="preserveStartEnd"
            minTickGap={24}
          />
          <YAxis
            tickLine={false}
            axisLine={false}
            tick={{ fontSize: 11 }}
            allowDecimals={false}
            width={44}
            tickFormatter={(v: number) => axisLabel(v, metric.format)}
          />
          <ChartTooltip
            cursor={{ fill: "hsl(var(--muted))" }}
            content={
              <ChartTooltipContent
                hideIndicator
                formatter={(value) => {
                  const n = Number(value) || 0;
                  const text = metric.format ? format(n) : `${format(n)} ${unit}${n === 1 ? "" : "s"}`;
                  return <span className="font-medium tabular-nums text-foreground">{text.trim()}</span>;
                }}
              />
            }
          />
          <Bar dataKey="value" fill="var(--color-value)" radius={2} maxBarSize={28} />
        </BarChart>
      </ChartContainer>
    </div>
  );
}
